<template>
  <section
    id="nearby-bookable"
    class="nearby-bookable"
    :class="[
      { 'loading-placeholder': isLoading },
      { 'no-results': isMountedAndLoaded && campsites.length == 0 },
      { desktop: $isDesktop },
    ]"
  >
    <nearby-bookable-header v-if="!isLoading" v-bind="headerProps" />
    <div v-if="isLoading" class="loading">
      <translate tag="h3"> Nearest sites </translate>
      <loading-spinner />
    </div>
    <div v-else-if="hasError" class="loading error">
      Error loading nearby sites
    </div>
    <template v-else-if="campsites.length > 0">
      <client-only>
        <lazy-nearby-bookable-map
          v-scroll="throttleScroll"
          v-bind="mapProps"
          :class="{ 'map-ui-locked': isScrolling }"
          @marker-clicked="onMapMarkerClicked($event)"
        />
      </client-only>
      <nearby-bookable-list
        v-bind="listProps"
        :active-campsite="visibleCampsite"
        @campsite-changed="visibleCampsiteChanged($event)"
      />
    </template>
    <nearby-bookable-footer
      v-if="!isLoading && campsites.length > 0"
      v-bind="footerProps"
      :nearby-results="campsites.length > 0"
      class="nearby-bookable-footer"
    />
  </section>
</template>

<script lang="ts">
import { debounce, throttle } from 'lodash-es'
import { Distance } from '~/utility/geo/Distance'
import {
  getGeoCircleFromPointAndRange,
  getGeoPointFromWKTPoint,
} from '~/utility/geo/GeoLocation'
import { getPointFromStringOrDefault } from '~/utility/geo/latLng'
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'
import { scrollIntoView } from '~/utility/scroll'
import { logger } from '../../../../utility/logger'
import { Marker } from '../../../maps/components/pu-map/types'
import { routeToFilters } from '../../../pu-links/infrastructure/routeToFilters'
import {
  NearbyCampsitesMachineInterpreter,
  getNearbyCampsitesMachine,
} from '../machine'
import { NearbyCampsite, SearchFilters } from '../schemas'
import type NearbyBookableFooter from './NearbyBookableFooter.vue'
import type NearbyBookableHeader from './NearbyBookableHeader.vue'
import type NearbyBookableList from './NearbyBookableList.vue'

interface SimpleHierarchy {
  name: string
  path: string
}

@Component
class NearbyBookable extends Vue {
  @Prop({ required: true })
  point: string

  @Prop()
  name: string

  @Prop()
  radius: { km: number; mi: number }

  @Prop({ required: true })
  hierarchyPath: string

  @Prop({ default: 20 })
  maxResults: number

  @Prop({ default: false })
  basicListing: boolean

  @Prop()
  campsiteSlug: string

  @Prop()
  countryHierarchy: SimpleHierarchy

  @Prop()
  adminAreaHierarchy: SimpleHierarchy

  isDatedSearch?: boolean
  state: NearbyCampsitesMachineInterpreter['state'] | null = null
  campsites: NearbyCampsite[] = []
  visibleCampsite: string | null = null
  isMountedAndLoaded = false
  initialVisibilityEvent = true

  mounted() {
    logger('NearbyBookable.mounted')
    getNearbyCampsitesMachine({
      searchFilters: this.searchFilters,
      slug: this.campsiteSlug,
      langCode: this.$route.params.lang || 'en-gb',
    })
      .onTransition((state) => {
        this.state = state
        this.campsites = state.context.campsites
        this.isDatedSearch = state.context.isDated
        // set in here to avoid race condition
        this.isMountedAndLoaded = true
      })
      .start()
  }

  visibleCampsiteChanged(slug: string) {
    // ignore the first visibility event, as this will be triggered on first render
    if (this.initialVisibilityEvent) {
      this.initialVisibilityEvent = false
      return
    }
    this.visibleCampsite = slug
  }

  get isLoading(): boolean {
    return this.state?.matches('fetching') || false
  }

  get hasError(): boolean {
    return this.state?.matches('error') || false
  }

  get mapProps(): Partial<NearbyBookableMap> {
    return {
      area: this.area,
      campsites: this.campsites,
      visibleCampsite: this.visibleCampsite ? this.visibleCampsite : undefined,
    }
  }

  get listProps(): Partial<NearbyBookableList> {
    return {
      campsites: this.campsites,
    }
  }

  get headerProps(): Partial<NearbyBookableHeader> {
    const filters = routeToFilters(this.$route)
    return {
      hasResults: this.campsites.length > 0,
      isDated: this.isDatedSearch,
      isFallbackSearch: filters.dates && !this.isDatedSearch,
      radius:
        this.radius ||
        Distance.new({ mi: Number(this.$config.public.defaultWithinRange) }),
      basicListing: this.basicListing,
      name: this.name,
      point: getGeoPointFromWKTPoint(this.point),
    }
  }

  get footerProps(): Partial<NearbyBookableFooter> {
    return {
      hierarchyText: this.seeMoreInHierarchy?.name || '',
      hierarchyPath: this.seeMoreInHierarchy?.path || '',
    }
  }

  private get seeMoreInHierarchy(): SimpleHierarchy | null {
    return this.hasNearbyResults
      ? this.adminAreaHierarchy
      : this.countryHierarchy
  }

  private get hasNearbyResults() {
    return this.campsites.length > 0
  }

  private get area() {
    const point = getPointFromStringOrDefault(this.point)
    const radius =
      this.radius ||
      Distance.new({ mi: Number(this.$config.public.defaultWithinRange) })
    return getGeoCircleFromPointAndRange(point, radius.mi)
  }

  private get searchFilters(): SearchFilters {
    const filters = routeToFilters(this.$route)
    return {
      ...filters,
      hierarchyPath: undefined,
      limit: this.maxResults,
    }
  }

  isScrolling = false
  private debounceStopScroll = debounce(this.stopScroll, 500)
  throttleScroll = throttle(this.handleScroll, 800)

  private stopScroll() {
    this.isScrolling = false
  }

  handleScroll() {
    this.isScrolling = true
    this.debounceStopScroll()
  }

  async onMapMarkerClicked({ slug }: Marker) {
    logger('NearbyBookable', 'onMapMarkerClicked', slug)
    await this.$nextTick()
    if (!this.$.isUnmounted) {
      scrollIntoView(`[data-campsite-slug="${slug}"]`, {
        behavior: 'auto',
      })
      this.visibleCampsiteChanged(slug)
    }
  }
}

export default toNative(NearbyBookable)
</script>

<style scoped>
.nearby-bookable {
  width: 100%;
  height: 100%;
  position: relative;
  background: var(--colour-page-background-light);
  border-top: 4px solid var(--colour-page-background);
}

.no-results {
  border-bottom: 4px solid var(--colour-page-background);
}

.nearby-bookable:not(.no-results) {
  /* min height to mitigate CLS */
  min-height: 208vw;
}

.nearby-bookable.top-position {
  border-top: none;
  border-bottom: 4px solid white;
}

.nearby-bookable-footer {
  margin-top: -2.5em;
  padding-bottom: 1em;
}

.loading-placeholder {
  min-height: 208vw;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
}

.map-ui-locked {
  pointer-events: none;
}

.loading {
  text-align: center;
  padding: 0 1.4em;
  color: var(--colour-dark-blue);
  font-size: var(--body);
}

/* desktop & tablet */
.nearby-bookable.desktop {
  background: white;
  isolation: isolate;
}

.nearby-bookable.desktop:not(.no-results) {
  /* min height to mitigate CLS */
  min-height: 60em;
}

.nearby-bookable.desktop :deep(.nearby-title),
.nearby-bookable.desktop :deep(.nearby-subhead) {
  margin: auto;
  max-width: var(--page-width-max);
  padding: 2.5em var(--page-width-padding);
}

.nearby-bookable.desktop :deep(.nearby-subhead) {
  padding: 0 var(--page-width-padding);
}

.desktop .loading-placeholder {
  min-height: 60em;
}

.nearby-bookable.desktop.bottom-position {
  border-top: none;
}

.nearby-bookable.desktop :deep(.map-wrapper) {
  height: 44em;
  width: 100%;
}

.desktop .nearby-bookable-footer {
  margin-top: 0;
}

.desktop.no-results {
  padding-bottom: 2em;
}

.nearby-bookable.desktop :deep(.nearby-sites-results) {
  padding: 2em 0 2em calc(var(--page-width-padding) - 1.4em);
  scroll-padding-left: calc(var(--page-width-padding) - 1.4em);
  margin-top: -20em;
}

/* > 1600 */
@media only screen and (min-width: 1600px) {
  .nearby-bookable.desktop :deep(.nearby-sites-results) {
    padding: 2em 0 2em
      calc(
        (100vw - var(--page-width-max)) / 2 + var(--page-width-padding) - 1.4em
      );
    scroll-padding-left: calc(
      (100vw - var(--page-width-max)) / 2 + var(--page-width-padding) - 1.4em
    );
  }
}
</style>
